import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { Crown, CheckCircle2, Loader2 } from 'lucide-react';
import { GoogleButton } from '@/components/ui/GoogleButton';
import { useAuth } from '@/contexts/AuthContext';
import { Turnstile } from '@marsidev/react-turnstile';
import { getEntitlements } from '@/utils/entitlements';

type Step = 'account' | 'plan' | 'child' | 'checkout';

type TierLevel = 'foundation' | 'intermediate' | 'advanced';

interface ChildForm {
  name: string;
  age: string;
  level: TierLevel;
  hasOwnEmail?: boolean;
  email?: string;
  existingUserId?: string;   // seeded (unpaid-holder re-checkout): renders read-only, links not duplicates
}

const getTierLevel = (age: number): TierLevel => {
  if (age >= 16) return 'advanced';
  if (age >= 12) return 'intermediate';
  return 'foundation';
};

const slideVariants = {
  enter: { opacity: 0, x: 40 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -40 },
};

// ── Dark family (ChildSubjectsPage reference): BLUE = actions, GOLD = Elite
// brand, dark navy surfaces. Self-contained — immune to global CSS state.
const CANVAS = '#0A0F1E'
const CARD = '#0F1930'
const INK = '#F2F5FA'
const MUTED = '#9AA7BC'
const BORDER = 'rgba(255,255,255,0.10)'
const GOLD = '#E8C268'
const SAGE_GREEN = '#0EA5E9'              // action colour — electric blue
const SAGE_LIGHT = 'rgba(14,165,233,0.10)'
const SAGE_ACCENT = '#7DD3FC'             // light-blue eyebrow/label accent

const steps: Step[] = ['account', 'plan', 'child', 'checkout'];

// Real Turnstile site key only. When unset: NO widget renders, NO token is
// required, and turnstileToken stays null — which register-education treats
// as dormant (verifies only when token AND secret exist). The old test-key
// fallback issued dummy tokens that the live secret rejected → "Captcha
// verification failed" for every guest, killing the shell funnel. Arming
// later = set VITE_TURNSTILE_SITE_KEY, zero code changes.
const TURNSTILE_SITE_KEY = (import.meta as any).env?.VITE_TURNSTILE_SITE_KEY || '';

// ─── Sub-components ────────────────────────────────────────────────────────

function StepInput({
  label, value, onChange, type = 'text', placeholder, required = false, hint,
}: {
  label: string; value: string; onChange: (v: string) => void;
  type?: string; placeholder?: string; required?: boolean; hint?: string;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: MUTED, marginBottom: 6 }}>{label}</label>
      <input
        type={type} value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} required={required}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        style={{
          width: '100%', padding: '12px 16px', boxSizing: 'border-box',
          border: `2px solid ${focused ? SAGE_GREEN : BORDER}`,
          borderRadius: 10, fontSize: 15, outline: 'none',
          background: CARD, color: INK,
          transition: 'border-color 0.15s',
        }}
      />
      {hint && <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────

export default function HomeschoolEnrolPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, userProfile, isLoading: authLoading, signInWithGoogle } = useAuth();
  const [step, setStep] = useState<Step>('account');
  const [loading, setLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [plans, setPlans] = useState<any[]>([]);
  const [searchParams] = useSearchParams();
  const [selectedPlan, setSelectedPlan] = useState<string>('homeschool_single');
  const [billingInterval, setBillingInterval] = useState<'month' | '6month' | 'year'>('month');
  const [childCredentials, setChildCredentials] = useState<{ name: string; email: string; password: string }[]>([]);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [individualAge, setIndividualAge] = useState('');
  const canceledFromStripe = searchParams.get('canceled') === 'true';

  const isIndividual = searchParams.get('type') === 'individual';

  const LEVEL_LABELS: Record<TierLevel, string> = {
    foundation: selectedPlan === 'homeschool_single' ? 'Primary · typically ages 8–11' : 'Foundation — Beginners · typically ages 8–11',
    intermediate: selectedPlan === 'homeschool_single' ? 'Secondary · typically ages 12–15' : 'Intermediate — Developing Leaders · typically ages 12–15',
    advanced: selectedPlan === 'homeschool_single' ? 'Sixth Form / Senior · typically ages 16+' : 'Advanced — Adult & Executive Level · typically ages 16+',
  };

  const STEP_LABELS: Record<Step, string> = {
    account: 'Your Account',
    plan: 'Choose Plan',
    child: 'Child Details',
    checkout: 'Payment',
  };

  // Individual: 3-step wizard (account → plan → checkout)
  // Family shell funnel: single step (account → dashboard directly)
  // mode=child from dashboard: full 4-step wizard
  const modeParam = searchParams.get('mode');
  const { isPaid: userIsPaid } = getEntitlements(userProfile);
  // Signed-in facility holder who hasn't paid (shell parent, or lapsed) →
  // the FULL wizard is their upgrade path. Never bounce them away.
  const unpaidFacilityHolder = !!userProfile?.facility_id && !userIsPaid;
  const isFamilyShell = !isIndividual && modeParam !== 'child' && !unpaidFacilityHolder;
  // ── Signed-in users skip the account step — they already have one ──
  // (Jacob lesson: only act once auth has resolved, never on a null user.)
  // mode=child and unpaid facility holders land straight on plan selection.
  const skipAccountStep = !!user && !authLoading && !isIndividual;
  const visibleSteps = isIndividual
    ? (['account', 'plan', 'checkout'] as Step[])
    : skipAccountStep
    ? (['plan', 'child', 'checkout'] as Step[])
    : isFamilyShell
    ? (['account'] as Step[])
    : steps;
  useEffect(() => {
    if (skipAccountStep && step === 'account' && (modeParam === 'child' || unpaidFacilityHolder)) {
      setStep('plan');
    }
  }, [skipAccountStep, step, modeParam, unpaidFacilityHolder]);

  // ── Existing-user redirect ─────────────────────────────────────────────────
  // Case A: Already has a facility → go to subscription management
  // Case B: Google OAuth user, no facility yet → call shell mode to provision
  //         facility silently, then send to parent dashboard
  useEffect(() => {
    if (authLoading || !userProfile) return;

    if (userProfile.facility_id) {
      // Paid parent → manage subscription (adding children goes through the
      // billed AddChildForm at 50%, not this wizard).
      if (userIsPaid) {
        const planParam = searchParams.get('plan');
        const dest = planParam
          ? `/homeschool/subscription?plan=${planParam}`
          : '/homeschool/subscription';
        navigate(dest, { replace: true });
        return;
      }
      // Unpaid facility holder:
      //  - children exist (abandoned checkout / lapsed) → subscription page re-checkout
      //  - zero children (shell parent) → STAY. This wizard is the upgrade path.
      const facilityId = userProfile.facility_id;   // ← narrowed to string here
      let cancelled = false;
      (async () => {
        const { data: existing } = await supabase
          .from('users')
          .select('id, name, age, email')
          .eq('facility_id', facilityId)
          .eq('facility_role', 'member');
        if (cancelled) return;
        if ((existing ?? []).length > 0) {
          setExistingChildren((existing ?? []).map((c: any) => ({
            id: c.id, name: c.name || 'Child', age: c.age ?? null, email: c.email,
          })));
          // Seed the stepper ONLY in wizard mode (mode=child came from the
          // subscription page; a bare /homeschool/enrol visit for an unpaid
          // holder stays on the redirect-to-subscription path).
          if (modeParam === 'child' || unpaidFacilityHolder) {
            setChildren((existing ?? []).map((c: any) => ({
              name: c.name || 'Child', age: c.age ? String(c.age) : '',
              level: getTierLevel(c.age ?? 12),
              email: c.email || undefined,           // the guard's match key
              existingUserId: c.id,                   // read-only rendering flag
            })));
          } else {
            navigate('/homeschool/subscription', { replace: true });
          }
        }
      })();
      return () => { cancelled = true };
    }

    // Guard: paid SOLO learners must never be shelled — send them to their home.
    const soloPlan =
      (userProfile?.preferences as any)?.billing?.plan || userProfile?.active_plan || '';
    if (user && ['homeschool_single', 'edu_elite', 'edu_bundle'].includes(soloPlan)) {
      navigate('/learn', { replace: true });
      return;
    }

    // Shell mode: any authenticated user with no facility yet gets provisioned silently.
    // Guard: established platform users (who have existing conversations) are NOT auto-shelled.
    if (user && !userProfile.facility_id) {
      let cancelled = false;
      (async () => {
        try {
          const { count } = await supabase
            .from('conversations')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', user.id);

          if ((count ?? 0) > 0 || cancelled) return;

          const { data: { session } } = await supabase.auth.getSession();
          if (!session?.access_token || cancelled) return;
          const name = (
            user.user_metadata?.full_name ||
            user.user_metadata?.name ||
            user.email?.split('@')[0] ||
            'Parent'
          ) as string;
          await supabase.functions.invoke('register-education', {
            headers: { Authorization: `Bearer ${session.access_token}` },
            body: { parent_name: name, email: user.email || '', mode: 'shell' },
          });
          if (!cancelled) navigate('/homeschool/parent?welcome=1', { replace: true });
        } catch { /* non-fatal — let wizard continue if shell fails */ }
      })();
      return () => { cancelled = true; };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.facility_id, user?.id, authLoading, userProfile, userIsPaid]);

  // ── Load plans from DB (prices managed in platform admin) ─────────────────
  useEffect(() => {
    const planParam = searchParams.get('plan');
    if (planParam && ['homeschool_single', 'edu_elite', 'edu_bundle'].includes(planParam)) {
      setSelectedPlan(planParam);
    }
  }, [searchParams]);

  useEffect(() => {
    async function fetchPricing() {
      const { data } = await supabase
        .from('subscription_plans')
        .select('*')
        .in('plan_key', ['homeschool_single', 'edu_elite', 'edu_bundle'])
        .eq('is_active', true);
      if (data) setPlans(data);
    }
    fetchPricing();
  }, []);

  // ── Form state ─────────────────────────────────────────────────────────────
  const [parentName, setParentName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [children, setChildren] = useState<ChildForm[]>([{ name: '', age: '', level: 'foundation' }]);
  // Existing facility children (unpaid-holder re-checkout) — pre-seeded into the
  // stepper so the subscription covers them; flagged so they render read-only
  // and register-education links rather than duplicates them.
  const [existingChildren, setExistingChildren] = useState<{ id: string; name: string; age: number | null; email: string }[]>([]);

  // Auto-populate from signed-in user (e.g. via Google OAuth)
  useEffect(() => {
    if (user) {
      if (user.email && !email) setEmail(user.email);
      const name = (user.user_metadata?.full_name || user.user_metadata?.name || userProfile?.name || '') as string;
      if (name && !parentName) setParentName(name);
    }
  }, [user, userProfile]);

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    setError('');
    try {
      try {
        localStorage.setItem('sage_auth_return_path', window.location.pathname + window.location.search);
      } catch {}
      const { error: gError } = await signInWithGoogle();
      if (gError) setError((gError as any)?.message || 'Google sign-in failed');
    } catch (err: any) {
      setError(err?.message || 'Google sign-in failed');
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const updateChild = (i: number, field: keyof ChildForm, value: string) => {
    const updated = [...children];
    updated[i] = { ...updated[i], [field]: value };
    if (field === 'age') {
      const ageNum = parseInt(value, 10);
      updated[i].level = isNaN(ageNum) ? 'foundation' : getTierLevel(ageNum);
    }
    setChildren(updated);
  };

  const addChild = () => setChildren([...children, { name: '', age: '', level: 'foundation' }]);
  const removeChild = (i: number) => setChildren(children.filter((_, j) => j !== i));

  // ── Pricing helpers (from DB, prices managed in platform admin) ────────────
  const activePlanData = plans.find(p => p.plan_key === selectedPlan);
  const priceMonthly: number = Number(activePlanData?.price_monthly) || (
    selectedPlan === 'edu_elite' ? 45 : selectedPlan === 'edu_bundle' ? 59 : 35
  );
  const priceSixMonthly: number = Number(activePlanData?.price_six_monthly) || Math.round(priceMonthly * 5.4);
  const priceYearly: number = Number(activePlanData?.price_yearly) || Math.round(priceMonthly * 9.6);

  const basePrice =
    billingInterval === 'year' ? Math.round(priceYearly / 12) :
    billingInterval === '6month' ? Math.round(priceSixMonthly / 6) :
    priceMonthly;

  const siblingCount = Math.max(0, children.length - 1);
  const totalMonthly = basePrice + siblingCount * Math.round(basePrice * 0.5);

  // ── Step navigation ────────────────────────────────────────────────────────
  async function handleAccountNext(e: React.FormEvent) {
    e.preventDefault();
    if (!parentName || !email || (!user && (!password || password.length < 8))) {
      setError(t('homeschool.enrol.passwordHint'));
      return;
    }
    setError('');

    // Individual path: still uses the full plan → checkout wizard
    if (isIndividual) { setStep('plan'); return; }

    // mode=child (enrol additional child from dashboard): full wizard too
    if (modeParam === 'child' || unpaidFacilityHolder) { setStep('plan'); return; }

    // ── Family shell funnel: create account (if guest) → shell → dashboard ──
    setLoading(true);
    try {
      let session = (await supabase.auth.getSession()).data.session;

      // Guest: create the auth account client-side so a session exists immediately
      // (email confirmation is disabled in our Supabase project → session returns at once)
      if (!user && !session) {
        const { error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { name: parentName } },
        });
        if (signUpError) {
          if (/already registered|already exists|email.*exists/i.test(signUpError.message)) {
            setError(t('homeschool.enrol.emailExists'));
            setLoading(false);
            return;
          }
          throw signUpError;
        }
        session = (await supabase.auth.getSession()).data.session;
      }

      const { data, error: fnError } = await supabase.functions.invoke('register-education', {
        headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
        body: {
          parent_name: parentName,
          email: user?.email || email,
          mode: 'shell',
          captchaToken: turnstileToken,
        },
      });

      if (fnError) {
        let msg = fnError.message;
        try {
          if ('context' in fnError && fnError.context) {
            const body = await (fnError.context as Response).json();
            if (body?.error) msg = body.error;
          }
        } catch { /* non-fatal */ }
        throw new Error(msg);
      }
      if (data?.error) throw new Error(data.error);

      navigate('/homeschool/parent?welcome=1', { replace: true });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('homeschool.enrol.errorCreate');
      setError(msg === 'email_exists' ? t('homeschool.enrol.emailExists') : msg);
      setLoading(false);
    }
  }

  // ── Main submission → calls register-education edge function ───────────────
  async function handleSubmit() {
    setLoading(true);
    setError('');
    setStep('checkout');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error: fnError } = await supabase.functions.invoke('register-education', {
        headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
        body: {
          parent_name: parentName || (user?.user_metadata?.full_name || user?.user_metadata?.name || userProfile?.name || 'Parent'),
          email: user?.email || email,
          // Family-path: account was created client-side in handleAccountNext (signUp).
          // Only individuals (who never hit handleAccountNext's shell branch) need to send a password.
          password: isIndividual ? password : '',
          plan_key: selectedPlan,
          billing_interval: billingInterval,
          children: isIndividual ? [] : children.map(c => ({
            name: c.name,
            age: c.age,
            email: c.hasOwnEmail && c.email?.trim() ? c.email.trim() : undefined,
          })),
          is_individual: isIndividual,
          individual_age: isIndividual ? individualAge || undefined : undefined,
          captchaToken: turnstileToken,
          ref_code: (typeof localStorage !== 'undefined' ? localStorage.getItem('sage_affiliate_ref') : null) || undefined
        },
      });

      if (fnError) {
        let errorMsg = fnError.message;
        try {
          if ('context' in fnError && fnError.context) {
            const body = await (fnError.context as Response).json();
            if (body?.error) errorMsg = body.error;
          }
        } catch { /* non-fatal */ }
        throw new Error(errorMsg);
      }
      if (!data?.checkout_url) throw new Error('No checkout URL returned');

      // Bug #3: stash child credentials in sessionStorage before redirect
      // Parent will see a "Save these details" panel when Stripe returns
      if (data.children && data.children.length > 0) {
        try {
          sessionStorage.setItem(
            'sw_child_credentials',
            JSON.stringify(data.children.map((c: any) => ({
              name: c.name,
              email: c.email,
              password: c.password,
            }))),
          );
          setChildCredentials(data.children);
        } catch { /* sessionStorage may be blocked — non-fatal */ }
      }

      // Redirect to Stripe
      window.location.href = data.checkout_url;

    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('homeschool.enrol.errorCreate');

      // Surface friendly messages for known errors
      if (msg === 'email_exists') {
        setError(t('homeschool.enrol.emailExists'));
        setStep('account');
      } else {
        setError(msg);
        setStep(isIndividual ? 'plan' : 'child');
      }
    } finally {
      setLoading(false);
    }
  }

  const stepIdx = visibleSteps.indexOf(step);
  const progressPct = ((stepIdx + 1) / visibleSteps.length) * 100;

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: "'Inter', system-ui, sans-serif", background: CANVAS, color: INK, minHeight: '100vh' }}>

      {/* ── Sticky nav ──────────────────────────────────────────────────────── */}
      <nav style={{
        background: CARD, borderBottom: `1px solid ${BORDER}`,
        padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 50,
      }}>
        <button onClick={() => navigate('/')} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(135deg, #7a5c20, #C9A84C)',
            border: '1px solid rgba(201,168,76,0.3)',
            boxShadow: '0 2px 8px rgba(201,168,76,0.2)',
          }}>
            <Crown size={18} color="#fff" />
          </div>
          <div style={{ textAlign: 'left' }}>
            <span style={{ fontWeight: 800, fontSize: 16, color: INK, display: 'block', lineHeight: 1.1 }}>Still Waters</span>
            <span style={{ fontWeight: 700, fontSize: 10, color: GOLD, textTransform: 'uppercase', letterSpacing: '0.15em', display: 'block' }}>
              {selectedPlan === 'edu_elite' ? 'Elite Academy' : 'Academy'}
            </span>
          </div>
        </button>

        {/* Step indicators */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {visibleSteps.map((s, i) => (
            <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: i <= stepIdx ? SAGE_GREEN : 'rgba(255,255,255,0.08)',
                color: i <= stepIdx ? '#fff' : MUTED,
                fontSize: 13, fontWeight: 700,
                transition: 'background 0.3s', flexShrink: 0,
              }}>
                {i < stepIdx ? <CheckCircle2 size={14} /> : i + 1}
              </div>
              <span className="hidden sm:inline" style={{ fontSize: 13, color: i <= stepIdx ? SAGE_GREEN : MUTED, fontWeight: i === stepIdx ? 700 : 400 }}>
                {STEP_LABELS[s]}
              </span>
              {i < visibleSteps.length - 1 && <span style={{ color: '#ddd', fontSize: 12 }}>›</span>}
            </div>
          ))}
        </div>
      </nav>

      {/* ── Progress bar ──────────────────────────────────────────────────────── */}
      <div style={{ height: 3, background: 'rgba(255,255,255,0.08)' }}>
        <motion.div
          animate={{ width: `${progressPct}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          style={{ height: '100%', background: SAGE_GREEN, borderRadius: '0 100px 100px 0' }}
        />
      </div>

      {/* ── Content ───────────────────────────────────────────────────────────── */}
      <div style={{ maxWidth: 560, margin: '0 auto', padding: '52px 24px 80px' }}>

        {/* Stripe cancel banner */}
        {canceledFromStripe && step === 'account' && (
          <motion.div
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            style={{ background: 'rgba(251,191,36,0.10)', border: '1.5px solid rgba(251,191,36,0.35)', borderRadius: 12, padding: '14px 18px', marginBottom: 24, fontSize: 14, color: '#FDE68A' }}
          >
            Payment was cancelled — your details are saved. Pick up where you left off, or{' '}
            <button onClick={() => navigate('/signin')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: SAGE_GREEN, fontWeight: 700 }}>
              sign in
            </button>{' '}
            to retry from your dashboard.
          </motion.div>
        )}

        <AnimatePresence>
          {error && (
            <motion.div
              key="err"
              initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              style={{ background: 'rgba(239,68,68,0.10)', border: '1.5px solid rgba(239,68,68,0.35)', borderRadius: 12, padding: '14px 18px', marginBottom: 24, fontSize: 14, color: '#fca5a5' }}
            >
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence mode="wait">

          {/* ── STEP 1: Account ───────────────────────────────────────────────── */}
          {step === 'account' && (
            <motion.form
              key="account"
              variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}
              onSubmit={handleAccountNext}
            >
              <div style={{ marginBottom: 36 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: SAGE_ACCENT, letterSpacing: '1.5px', marginBottom: 12 }}>{t('common.step_n_of_m', { n: 1, m: visibleSteps.length })}</div>
                <h1 style={{ color: INK, fontSize: 34, fontWeight: 800, letterSpacing: '-1.5px', marginBottom: 10, lineHeight: 1.1 }}>
                  {isIndividual ? 'Create your account' : t('homeschool.enrol.step1')}
                </h1>
                <p style={{ color: MUTED, fontSize: 15, lineHeight: 1.6 }}>
                  {isIndividual ? 'One account for your own learning journey.' : t('homeschool.enrol.subtitle')}
                </p>
                <p style={{ marginTop: 10, fontSize: 13, color: MUTED }}>
                  Create a free account to explore first, or{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/prep')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: SAGE_ACCENT, fontWeight: 700, textDecoration: 'underline', fontSize: 13, padding: 0 }}
                  >
                    Try 4 free foundation courses →
                  </button>
                </p>
              </div>

              <div style={{ marginBottom: 22 }}>
                <GoogleButton
                  onClick={handleGoogleSignIn}
                  disabled={loading || isGoogleLoading}
                  loading={isGoogleLoading}
                >
                  {isIndividual ? 'Continue with Google' : 'Continue as Parent with Google'}
                </GoogleButton>

                <div style={{ display: 'flex', alignItems: 'center', margin: '22px 0 18px', gap: 12 }}>
                  <div style={{ flex: 1, height: 1, background: BORDER }} />
                  <span style={{ fontSize: 12, color: MUTED, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    or continue with email
                  </span>
                  <div style={{ flex: 1, height: 1, background: BORDER }} />
                </div>
              </div>

              {user && (
                <div style={{
                  background: 'rgba(74,222,128,0.10)', border: '1.5px solid rgba(74,222,128,0.30)', borderRadius: 12,
                  padding: '12px 16px', marginBottom: 18, display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#4ADE80', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Signed In via Google</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#86EFAC' }}>{user.email}</div>
                  </div>
                  <span style={{ fontSize: 12, color: '#4ADE80', fontWeight: 600 }}>Connected</span>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <StepInput label={isIndividual ? 'Your full name' : t('homeschool.enrol.namePlaceholder')} value={parentName} onChange={setParentName} placeholder="Sarah Johnson" required />
                {isIndividual && (
                  <StepInput
                    label={t('homeschool.your_age_optional')}
                    value={individualAge}
                    onChange={setIndividualAge}
                    placeholder="e.g. 34"
                    type="number"
                    hint="Helps us tailor the teaching style to you."
                  />
                )}
                <StepInput label={t('homeschool.enrol.emailPlaceholder')} value={email} onChange={setEmail} placeholder="sarah@example.com" type="email" required />
                {!user && (
                  <StepInput label={t('common.password')} value={password} onChange={setPassword} placeholder={t('homeschool.enrol.passwordPlaceholder')} type="password" required hint={t('homeschool.enrol.passwordHint')} />
                )}
              </div>

              {/* Turnstile — armed only when a real site key is configured;
                  dormant otherwise, matching the server's dormant-until-both design */}
              {!user && TURNSTILE_SITE_KEY && (
                <div style={{ margin: '20px 0', display: 'flex', justifyContent: 'center' }}>
                  <Turnstile
                    siteKey={TURNSTILE_SITE_KEY}
                    onSuccess={(token) => setTurnstileToken(token)}
                    onError={() => setError('Bot protection verification failed. Please try again.')}
                  />
                </div>
              )}

              <motion.button
                type="submit"
                disabled={loading || (!user && !!TURNSTILE_SITE_KEY && !turnstileToken)}
                whileHover={{ scale: loading ? 1 : 1.01 }} whileTap={{ scale: loading ? 1 : 0.98 }}
                style={{
                  marginTop: 16, width: '100%', background: SAGE_GREEN, color: '#fff',
                  border: 'none', padding: '15px', borderRadius: 12,
                  cursor: loading || (!user && !!TURNSTILE_SITE_KEY && !turnstileToken) ? 'not-allowed' : 'pointer',
                  fontSize: 16, fontWeight: 700, boxShadow: '0 4px 20px rgba(14,165,233,0.25)',
                  opacity: loading || (!user && !!TURNSTILE_SITE_KEY && !turnstileToken) ? 0.7 : 1,
                  transition: 'opacity 0.2s',
                }}
              >
                {loading
                  ? t('homeschool.enrol.creating')
                  : isFamilyShell
                  ? 'Create My Family Hub →'
                  : `${t('homeschool.enrol.continue')} →`}
              </motion.button>

              <p style={{ textAlign: 'center', marginTop: 18, fontSize: 13, color: MUTED }}>
                {t('common.already_have_account')}{' '}
                <button onClick={() => navigate('/signin')} type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', color: SAGE_GREEN, fontWeight: 700 }}>
                  {t('homeschool.enrol.signIn')}
                </button>
              </p>
            </motion.form>
          )}

          {/* ── STEP 2: Plan ──────────────────────────────────────────────────── */}
          {step === 'plan' && (
            <motion.div
              key="plan"
              variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}
            >
              <div style={{ marginBottom: 36 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: SAGE_ACCENT, letterSpacing: '1.5px', marginBottom: 12 }}>{t('common.step_n_of_m', { n: 2, m: visibleSteps.length })}</div>
                <h1 style={{ color: INK, fontSize: 34, fontWeight: 800, letterSpacing: '-1.5px', marginBottom: 10, lineHeight: 1.1 }}>
                  {isIndividual ? t('homeschool.enrol.elitePlanTitle') : t('homeschool.enrol.learningPathTitle')}
                </h1>
                <p style={{ color: MUTED, fontSize: 15, lineHeight: 1.6 }}>
                  {isIndividual ? t('homeschool.enrol.elitePlanSub') : t('homeschool.enrol.learningPathSub')}
                </p>
              </div>

              {/* Billing interval toggle */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 24, background: 'rgba(255,255,255,0.06)', borderRadius: 14, padding: 6 }}>
                {([
                  { key: 'month', label: t('homeschool.enrol.plans.monthly') },
                  { key: '6month', label: t('homeschool.enrol.plans.sixMonthly') },
                  { key: 'year', label: t('homeschool.enrol.plans.yearly') },
                ] as { key: typeof billingInterval; label: string }[]).map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setBillingInterval(opt.key)}
                    style={{
                      flex: 1, padding: '8px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
                      fontWeight: 700, fontSize: 13,
                      background: billingInterval === opt.key ? SAGE_GREEN : 'transparent',
                      color: billingInterval === opt.key ? '#fff' : '#888',
                      transition: 'all 0.2s',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Plan cards — prices from DB */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 32 }}>
                {[
                  {
                    key: 'homeschool_single', name: 'Standard Education', icon: '📚',
                    desc: 'Full 84-subject standard curriculum.',
                    monthly: Number(plans.find(p => p.plan_key === 'homeschool_single')?.price_monthly) || 35,
                    sixMonthly: Number(plans.find(p => p.plan_key === 'homeschool_single')?.price_six_monthly) || 189,
                    yearly: Number(plans.find(p => p.plan_key === 'homeschool_single')?.price_yearly) || 336,
                  },
                  {
                    key: 'edu_elite', name: 'Elite Academy', icon: '👑', isElite: true,
                    desc: 'Etiquette, finance, diplomacy & leadership. All 30 courses.',
                    monthly: Number(plans.find(p => p.plan_key === 'edu_elite')?.price_monthly) || 45,
                    sixMonthly: Number(plans.find(p => p.plan_key === 'edu_elite')?.price_six_monthly) || 243,
                    yearly: Number(plans.find(p => p.plan_key === 'edu_elite')?.price_yearly) || 432,
                  },
                  {
                    key: 'edu_bundle', name: 'Complete Bundle', icon: '🌟', isBundle: true,
                    desc: 'Everything: 84 Standard + 30 Elite courses.',
                    monthly: Number(plans.find(p => p.plan_key === 'edu_bundle')?.price_monthly) || 59,
                    sixMonthly: Number(plans.find(p => p.plan_key === 'edu_bundle')?.price_six_monthly) || 318,
                    yearly: Number(plans.find(p => p.plan_key === 'edu_bundle')?.price_yearly) || 565,
                  },
                ].map((p) => {
                  const displayPrice =
                    billingInterval === 'year' ? Math.round(p.yearly / 12) :
                    billingInterval === '6month' ? Math.round(p.sixMonthly / 6) :
                    p.monthly;
                  const billedLabel =
                    billingInterval === 'year' ? `$${p.yearly}/yr` :
                    billingInterval === '6month' ? `$${p.sixMonthly}/6mo` :
                    null;
                  const accent = (p as any).isElite || (p as any).isBundle ? GOLD : SAGE_GREEN;
                  return (
                    <button
                      key={p.key}
                      onClick={() => setSelectedPlan(p.key)}
                      style={{
                        width: '100%', padding: '24px', borderRadius: 20,
                        border: `2px solid ${selectedPlan === p.key ? accent : BORDER}`,
                        background: selectedPlan === p.key ? ((p as any).isElite || (p as any).isBundle ? `${GOLD}14` : SAGE_LIGHT) : CARD,
                        textAlign: 'left', cursor: 'pointer', transition: 'all 0.2s',
                        display: 'flex', alignItems: 'center', gap: 20, position: 'relative',
                      }}
                    >
                      <div style={{ width: 54, height: 54, borderRadius: 14,background: (p as any).isElite || (p as any).isBundle ? `${GOLD}15` : SAGE_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>
                        {p.icon}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 800, fontSize: 17, color: INK, marginBottom: 2 }}>{p.name}</div>
                        <div style={{ fontSize: 13, color: MUTED }}>{p.desc}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 800, fontSize: 20, color: accent }}>${displayPrice}</div>
                        <div style={{ fontSize: 11, color: MUTED, fontWeight: 700 }}>/MONTH</div>
                        {billedLabel && <div style={{ fontSize: 10, color: MUTED, fontWeight: 600, marginTop: 2 }}>{t('homeschool.enrol.plans.billed', { price: billedLabel })}</div>}
                      </div>
                      {selectedPlan === p.key && (
                        <div style={{
                          position: 'absolute', top: -10, right: 20, background: accent,
                          color: '#fff', fontSize: 10, fontWeight: 900, padding: '4px 12px', borderRadius: 20,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        }}>SELECTED</div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Turnstile is now in the account step — removed from here */}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
                <button onClick={() => setStep('account')} style={{ background: 'none', color: INK, border: '2px solid rgba(255,255,255,0.15)', padding: '15px', borderRadius: 12, cursor: 'pointer', fontSize: 15, fontWeight: 600 }}>
                  ← {t('homeschool.enrol.back')}
                </button>
                <button
                  onClick={() => isIndividual ? handleSubmit() : setStep('child')}
                  style={{ background: SAGE_GREEN, color: '#fff', border: 'none', padding: '15px', borderRadius: 12, cursor: 'pointer', fontSize: 16, fontWeight: 700, boxShadow: '0 4px 20px rgba(14,165,233,0.25)' }}
                >
                  {isIndividual ? 'Proceed to Payment →' : `${t('homeschool.enrol.continue')} →`}
                </button>
              </div>
            </motion.div>
          )}

          {/* ── STEP 3: Children (family path only) ───────────────────────────── */}
          {step === 'child' && !isIndividual && (
            <motion.div
              key="child"
              variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}
            >
              <div style={{ marginBottom: 36 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: SAGE_ACCENT, letterSpacing: '1.5px', marginBottom: 12 }}>{t('common.step_n_of_m', { n: 3, m: 4 })}</div>
                <h1 style={{ color: INK, fontSize: 34, fontWeight: 800, letterSpacing: '-1.5px', marginBottom: 10, lineHeight: 1.1 }}>
                  {t('homeschool.enrol.step3')}
                </h1>
                <p style={{ color: MUTED, fontSize: 15, lineHeight: 1.6 }}>
                  {t('homeschool.landing.howItWorks.steps.1.desc')}
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {children.map((child, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
                    style={{ background: CARD, border: '1.5px solid BORDER', borderRadius: 16, padding: '24px', position: 'relative', boxShadow: '0 2px 12px rgba(0,0,0,0.30)' }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 14, color: SAGE_ACCENT, marginBottom: 16, letterSpacing: '0.5px' }}>
                      {t('homeschool.enrol.childTitle')} {i + 1}
                      {i > 0 && (
                        <span style={{ marginLeft: 8, background: 'rgba(251,191,36,0.25)', color: '#FDE68A', fontSize: 11, padding: '2px 8px', borderRadius: 20, fontWeight: 700 }}>
                          {t('homeschool.enrol.siblingDiscountTitle')}
                        </span>
                      )}
                    </div>
                    {child.existingUserId ? (
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                        background: '#ecfdf5', border: '1.5px solid #a7f3d0', borderRadius: 10,
                        padding: '12px 14px',
                      }}>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: '#065f46' }}>{child.name}</div>
                          <div style={{ fontSize: 11.5, color: '#047857', marginTop: 2 }}>
                            Age {child.age || '—'} · already enrolled — this subscription covers them
                          </div>
                        </div>
                        <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', padding: '3px 9px', borderRadius: 999, background: '#d1fae5', color: '#065f46' }}>Covered</span>
                      </div>
                    ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 12 }}>
                      <StepInput label={t('homeschool.enrol.childName')} value={child.name} onChange={v => updateChild(i, 'name', v)} placeholder="Emma" required />
                      <StepInput label={t('homeschool.enrol.childAge')} value={child.age} onChange={v => updateChild(i, 'age', v)} placeholder="10" type="number" required />
                    </div>
                    )}
                    {child.age && (
                      <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                        style={{ marginTop: 12, background: SAGE_LIGHT, border: '1px solid rgba(14,165,233,0.30)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: SAGE_ACCENT, fontWeight: 600 }}
                      >
                        {t('homeschool.enrol.levelAutoAssigned', { level: LEVEL_LABELS[child.level] })}
                      </motion.div>
                    )}

                    {/* Own email option for older children */}
                    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px dashed #e0e0d8' }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: '#334155', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={!!child.hasOwnEmail}
                          onChange={e => {
                            const updated = [...children];
                            updated[i] = {
                              ...updated[i],
                              hasOwnEmail: e.target.checked,
                              email: e.target.checked ? (updated[i].email || '') : '',
                            };
                            setChildren(updated);
                          }}
                          style={{ width: 16, height: 16, accentColor: SAGE_GREEN, cursor: 'pointer' }}
                        />
                        <span>Does {child.name || 'this child'} have their own email?</span>
                        <span style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>(recommended for teens 12+)</span>
                      </label>
                      {child.hasOwnEmail && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} style={{ marginTop: 10 }}>
                          <StepInput
                            label={`${child.name || 'Child'}'s email address`}
                            value={child.email || ''}
                            onChange={v => {
                              const updated = [...children];
                              updated[i] = { ...updated[i], email: v };
                              setChildren(updated);
                            }}
                            placeholder="bobby@example.com"
                            type="email"
                            required={child.hasOwnEmail}
                            hint="Their sign-in magic link will be sent straight to their own inbox so they can log in independently."
                          />
                        </motion.div>
                      )}
                    </div>
                    {children.length > 1 && (
                      <button onClick={() => removeChild(i)} style={{ marginTop: 12, background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 13, padding: 0 }}>
                        {t('homeschool.enrol.removeChild')}
                      </button>
                    )}
                  </motion.div>
                ))}
              </div>

              {children.length < 5 && (
                <motion.button
                  whileHover={{ borderColor: SAGE_GREEN }}
                  onClick={addChild}
                  style={{ width: '100%', background: 'none', border: '2px dashed rgba(255,255,255,0.20)', borderRadius: 14, padding: '15px', cursor: 'pointer', fontSize: 14, color: MUTED, marginTop: 12, marginBottom: 24, transition: 'border-color 0.15s' }}
                >
                  {t('homeschool.enrol.addAnotherHelp')}
                </motion.button>
              )}

              {/* Price summary */}
              <div style={{ background: CARD, border: '1.5px solid BORDER', borderRadius: 16, padding: '20px 24px', marginBottom: 28 }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14 }}>{t('homeschool.enrol.summary')}</div>
                {children.map((child, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: MUTED, marginBottom: 6 }}>
                    <span>{child.name || `Child ${i + 1}`} — {LEVEL_LABELS[child.level]}</span>
                    <span style={{ fontWeight: 600 }}>
                      ${i === 0 ? basePrice : Math.round(basePrice * 0.5)}/mo
                    </span>
                  </div>
                ))}
                <div style={{ borderTop: '1px solid #f0f0e8', marginTop: 12, paddingTop: 12, display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 700, fontSize: 16 }}>{t('homeschool.enrol.total')}</span>
                  <span style={{ fontWeight: 800, fontSize: 18, color: SAGE_GREEN }}>${totalMonthly}/month</span>
                </div>
              </div>

              {/* Turnstile is now in the account step — removed from here */}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
                <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }} onClick={() => setStep('plan')} style={{ background: 'none', color:INK, border: '2px solid rgba(255,255,255,0.15)', padding: '14px', borderRadius: 12, cursor: 'pointer', fontSize: 15, fontWeight: 600 }}>
                  ← {t('homeschool.enrol.back')}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}
                  onClick={handleSubmit}
                  disabled={loading || children.some(c => !c.name || !c.age || (c.hasOwnEmail && !c.email?.trim()))}
                  style={{
                    background: SAGE_GREEN, color: '#fff', border: 'none',
                    padding: '14px', borderRadius: 12,
                    cursor: loading || children.some(c => !c.name || !c.age || (c.hasOwnEmail && !c.email?.trim())) ? 'not-allowed' : 'pointer',
                    fontSize: 15, fontWeight: 700,
                    opacity: loading || children.some(c => !c.name || !c.age || (c.hasOwnEmail && !c.email?.trim())) ? 0.6 : 1,
                    boxShadow: '0 4px 20px rgba(14,165,233,0.25)',
                  }}
                >
                  {loading ? t('homeschool.enrol.creating') : 'Proceed to Payment →'}
                </motion.button>
              </div>
            </motion.div>
          )}

          {/* ── STEP 4: Checkout loading screen ───────────────────────────────── */}
          {step === 'checkout' && (
            <motion.div
              key="checkout"
              variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}
              style={{ textAlign: 'center', paddingTop: 40 }}
            >
              <motion.div
                animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1.2, ease: 'linear' }}
                style={{ display: 'inline-flex', marginBottom: 28 }}
              >
                <Loader2 size={48} color={SAGE_GREEN} />
              </motion.div>
              <h2 style={{ fontSize: 26, fontWeight: 800, color: INK, marginBottom: 12, letterSpacing: '-0.5px' }}>
                Setting up your account…
              </h2>
              <p style={{ color: MUTED, fontSize: 15, lineHeight: 1.7, maxWidth: 380, margin: '0 auto' }}>
                We're creating your {isIndividual ? 'account' : 'family hub and enrolling your children'}, then redirecting you to secure payment.
                <br /><strong>{t('homeschool.do_not_close_this_tab')}</strong>
              </p>

              {/* Child credentials panel — shown once server returns them */}
              {childCredentials.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                  style={{
                    marginTop: 28, background: 'rgba(251,191,36,0.10)', border: '2px solid rgba(251,191,36,0.35)',
                    borderRadius: 16, padding: '20px 24px', textAlign: 'left', maxWidth: 440, margin: '28px auto 0',
                  }}
                >
                  <div style={{ fontWeight: 800, fontSize: 15, color: '#FDE68A', marginBottom: 10 }}>⚠️ Save your children's login details</div>
                  <p style={{ fontSize: 13, color: '#FDE68A', marginBottom: 14, lineHeight: 1.6 }}>
                    These are auto-generated. Screenshot or copy them now — you can reset them from the parent dashboard later.
                  </p>
                  {childCredentials.map((c, i) => (
                    <div key={i} style={{ background: CARD, borderRadius: 10, padding: '12px 14px', marginBottom: 8, fontSize: 13, fontFamily: 'monospace' }}>
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>{c.name}</div>
                      <div style={{ color: MUTED }}>📧 {c.email}</div>
                      <div style={{ color: MUTED }}>🔑 {c.password}</div>
                    </div>
                  ))}
                </motion.div>
              )}

              <div style={{ marginTop: 32, display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap', fontSize: 13, color: MUTED }}>
                {['🔐 Secure checkout', '🛡️ 256-bit encryption', '✅ Cancel anytime'].map(badge => (
                  <span key={badge} style={{ background: SAGE_LIGHT, padding: '6px 14px', borderRadius: 20, fontWeight: 600 }}>{badge}</span>
                ))}
              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </div>
    </div>
  );
}